import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Space, Table } from "antd";
import { toast } from "react-toastify";
import {
  RoleDto,
  listRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../../services/adminService";

export default function RolesAdmin() {
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<RoleDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setRoles(await listRoles());
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (values: { name: string; description?: string }) => {
    try {
      if (editing) {
        await updateRole(editing.id, { description: values.description });
        toast.success("Role updated");
      } else {
        await createRole(values);
        toast.success("Role created");
      }
      setEditing(null);
      setAdding(false);
      form.resetFields();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteRole(id);
      toast.success("Role deleted");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, r: RoleDto) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditing(r);
              form.setFieldsValue({ name: r.name, description: r.description });
            }}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this role?" onConfirm={() => onDelete(r.id)}>
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const modalOpen = adding || editing !== null;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          onClick={() => {
            setAdding(true);
            form.resetFields();
          }}
        >
          Add Role
        </Button>
      </div>
      <Table rowKey="id" loading={loading} dataSource={roles} columns={columns} pagination={false} />

      <Modal
        title={editing ? "Edit Role" : "Add Role"}
        open={modalOpen}
        onCancel={() => {
          setEditing(null);
          setAdding(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Role name is required" }]}
          >
            <Input disabled={!!editing} placeholder="e.g. appointment-page.view" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
